/**
 * Планировщик работы с History API.
 * Должен обходить различные ограничения в браузерах, связанные с изменением history.
 *
 * Известные проблемы в браузерах:
 *
 *   - С момента последнего вызова `history.back()` должно пройти не менее 50ms,
 *     иначе iOS Safari игнорирует последующий вызов `history.pushState()`.
 *
 *   - Между вызовами `history.back()` и `history.pushState()` (в любом порядке) должно пройти не менее 250ms,
 *     иначе Яндекс Браузер покажет попап «Вы действительно хотите закрыть страницу?» при попытке вернуться назад.
 *
 *   - Несколько вызовов `history.back()` должны агрегироваться в один вызов history.go(-n),
 *     иначе в iOS Safari сработает только первый вызов `history.back()`.
 *
 *   - Браузеры игнорируют любые изменения истории, если на странице ни разу не было user gesture.
 *     Это можно обойти, слушая события на документе, но пока не реализовано.
 *
 *   - Вызовы `history.go(-n)` и `history.back()` работают идентично,
 *     все ограничения выше справедливы и для `history.go(-n)`
 */

type Action = () => void;
type Validator = () => boolean;
type QueueCallback = () => void;

enum QueueItemType {
    Push = 'push',
    Back = 'back',
    Wait = 'wait',
}

type QueueItemPush = {
    type: QueueItemType.Push;
    key: string;
    action: Action;
    callback: QueueCallback;
};

type QueueItemBack = {
    type: QueueItemType.Back;
    key: string;
    count: number;
    action: Action;
    callback: QueueCallback;
    prevItem?: QueueItemBack;
};

type QueueItemWait = {
    type: QueueItemType.Wait;
    ms: number;
};

type QueueItem = QueueItemPush | QueueItemBack | QueueItemWait;
type Queue = Array<QueueItem>;

const queue: Queue = [];
let queueTimeout: ReturnType<typeof setTimeout> | null = null;

// Интервал, в течение которого накапливаются изменения истории,
// которые затем объединяются/фильтруются.
// Опытным путем подобрано 250ms, чтобы обойти все ограничения браузеров.
const aggregationInterval = 250;

function runQueue() {
    // Очередь уже выполняется
    if (queueTimeout) {
        return;
    }

    while (queue.length > 0) {
        const item = queue.shift();

        if (!item) {
            continue;
        }

        switch (item.type) {
            case QueueItemType.Push:
                item.action();
                item.callback();
                break;

            case QueueItemType.Back:
                item.action();
                item.callback();

                // Вызываем все обработчики для агрегированных вызовов history.back() -> history.go(-n)
                let prevItem = item.prevItem;
                while (prevItem) {
                    prevItem.callback();
                    prevItem = prevItem.prevItem;
                }

                break;

            case QueueItemType.Wait:
                queueTimeout = setTimeout(() => {
                    queueTimeout = null;
                    runQueue();
                }, item.ms);

                return;
        }
    }
}

function schedule(
    type: QueueItemType,
    key: string,
    action: Action = () => {},
    validator: Validator = () => true,
    callback: QueueCallback,
) {
    let prevItem = queue[queue.length - 1];
    if (prevItem && prevItem.type === QueueItemType.Wait) {
        // NOTE: считаем, что не может быть два ожидания подряд
        prevItem = queue[queue.length - 2];
    }

    if (type === QueueItemType.Push) {
        schedulePush(key, action, validator, prevItem, callback);
    }

    if (type === QueueItemType.Back) {
        scheduleBack(key, validator, prevItem, callback);
    }
}

function schedulePush(
    key: string,
    action: Action,
    validator: Validator,
    prevItem: QueueItem | undefined,
    callback: QueueCallback,
) {
    // Если ключ удаляется и затем сразу добавляется, то историю можно вообще не менять.
    if (prevItem && prevItem.type === QueueItemType.Back && prevItem.key === key) {
        // если «настоящий» prevItem – это ожидание, то его тоже нужно отменить
        if (queue[queue.length - 1]?.type === QueueItemType.Wait) {
            queue.pop();
        }

        // Отменяем history.back()
        queue.pop();
        prevItem.callback();

        // Если history.back() был агрегирован в history.go(-n), то
        // нужно восстановить в очереди предыдущий history.back()
        if (prevItem.prevItem) {
            queue.push(prevItem.prevItem);
        }

        // Просто вызываем обработчик для history.pushState(), но не добавляем в очередь
        callback();

        return;
    }

    // Даем возможность накопить вызовы history.pushState() и history.back(),
    // чтобы убирать последовательные вызовы на добавление и удаление ключа
    queue.push({
        type: QueueItemType.Wait,
        ms: aggregationInterval,
    });

    queue.push({
        type: QueueItemType.Push,
        key,
        action: () => {
            if (validator()) {
                action();
            }
        },
        callback,
    });
}

function scheduleBack(key: string, validator: Validator, prevItem: QueueItem | undefined, callback: QueueCallback) {
    // Если ключ добавляется и затем сразу удалятся, то историю можно вообще не менять.
    if (prevItem && prevItem.type === QueueItemType.Push && prevItem.key === key) {
        // если «настоящий» prevItem – это ожидание, то его тоже нужно отменить
        if (queue[queue.length - 1]?.type === QueueItemType.Wait) {
            queue.pop();
        }

        // Отменяем history.pushState()
        queue.pop();
        prevItem.callback();

        // Просто вызываем обработчик для history.back(), но не добавляем в очередь
        callback();

        return;
    }

    // Склеиваем вызовы history.back() в один вызов history.go(-n)
    if (prevItem && prevItem.type === QueueItemType.Back) {
        const count = prevItem.count + 1;

        // Удаляем предыдущий action с history.back();
        queue.pop();

        // Добавляем агрегированный action с history.go(-n);
        queue.push({
            type: QueueItemType.Back,
            key,
            count,
            action: () => {
                // NOTE: есть известная проблема, что валидатор вызывается только для последнего history.back(),
                // но пока нет известных кейсов, когда это нужно
                if (validator()) {
                    window.history.go(count * -1);
                }
            },
            callback,
            prevItem,
        });

        return;
    }

    // Даем возможность накопить вызовы history.back(), чтобы объединить их в history.go(-n)
    queue.push({
        type: QueueItemType.Wait,
        ms: aggregationInterval,
    });

    queue.push({
        type: QueueItemType.Back,
        key,
        count: 1,
        action: () => {
            if (validator()) {
                window.history.back();
            }
        },
        callback,
    });
}

function addToQueue(type: QueueItemType, key: string, action?: Action, validator?: Validator) {
    return new Promise<void>(resolve => {
        schedule(type, key, action, validator, resolve);
        runQueue();
    });
}

type HistoryPushOptions = {
    // Уникальный ключ действия над историей
    key: string;

    // Функция, которая вызывает history.pushState()
    action: Action;

    // Функция, которая проверяет возможность вызова `action`, когда до него дошла очередь
    validator?: Validator;
};
export function scheduleHistoryPush(options: HistoryPushOptions) {
    return addToQueue(QueueItemType.Push, options.key, options.action, options.validator);
}

type HistoryBackOptions = {
    // Уникальный ключ действия над историей
    key: string;

    // Функция, которая проверяет возможность вызова `action`, когда до него дошла очередь
    validator?: Validator;
};
export function scheduleHistoryBack(options: HistoryBackOptions) {
    return addToQueue(QueueItemType.Back, options.key, undefined, options.validator);
}
